import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../../../firebase";

const emptyForm = {
  title: "",
  url: "",
  type: "url",
  order: 10,
  isActive: true,
  fileName: "",
  storagePath: "",
};

export default function FooterLinksAdmin() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadLinks = async () => {
    const q = query(collection(db, "footerLinks"), orderBy("order", "asc"));
    const snap = await getDocs(q);

    const list = snap.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));

    setLinks(list);
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedFile(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setSelectedFile(null);

    setForm({
      title: item.title || "",
      url: item.url || "",
      type: item.type || "url",
      order: item.order || 10,
      isActive: item.isActive !== false,
      fileName: item.fileName || "",
      storagePath: item.storagePath || "",
    });
  };

  const uploadFooterFile = async (file) => {
    const safeName = file.name.replace(/[^\wа-яА-ЯіїєґІЇЄҐ.-]+/g, "_");
    const storagePath = `footer-files/${Date.now()}-${safeName}`;

    const fileRef = ref(storage, storagePath);

    await uploadBytes(fileRef, file);

    const downloadUrl = await getDownloadURL(fileRef);

    return {
      url: downloadUrl,
      fileName: file.name,
      storagePath,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      alert("Вкажіть назву посилання");
      return;
    }

    if (form.type === "url" && !form.url.trim()) {
      alert("Вкажіть посилання");
      return;
    }

    if (form.type === "file" && !selectedFile && !form.url) {
      alert("Завантажте файл");
      return;
    }

    setLoading(true);

    try {
      let finalUrl = form.url;
      let finalFileName = form.fileName || "";
      let finalStoragePath = form.storagePath || "";

      if (form.type === "file" && selectedFile) {
        const uploaded = await uploadFooterFile(selectedFile);

        finalUrl = uploaded.url;
        finalFileName = uploaded.fileName;
        finalStoragePath = uploaded.storagePath;

        // Якщо редагуємо старий файл — видаляємо попередній зі Storage
        if (editingId && form.storagePath) {
          try {
            await deleteObject(ref(storage, form.storagePath));
          } catch (error) {
            console.warn("Old file delete warning:", error);
          }
        }
      }

      const payload = {
        title: form.title.trim(),
        url: finalUrl.trim(),
        type: form.type,
        order: Number(form.order) || 0,
        isActive: Boolean(form.isActive),
        fileName: finalFileName,
        storagePath: finalStoragePath,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "footerLinks", editingId), payload);
      } else {
        await addDoc(collection(db, "footerLinks"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      await loadLinks();
      resetForm();
    } catch (error) {
      console.error("Footer link save error:", error);
      alert("Не вдалося зберегти посилання");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(`Видалити "${item.title}"?`);
    if (!ok) return;

    setLoading(true);

    try {
      if (item.storagePath) {
        try {
          await deleteObject(ref(storage, item.storagePath));
        } catch (error) {
          console.warn("File delete warning:", error);
        }
      }

      await deleteDoc(doc(db, "footerLinks", item.id));

      await loadLinks();

      if (editingId === item.id) {
        resetForm();
      }
    } catch (error) {
      console.error("Footer link delete error:", error);
      alert("Не вдалося видалити посилання");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "900px", padding: "30px" }}>
      <h2>Посилання у футері</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: "30px" }}>
        <div style={{ marginBottom: "15px" }}>
          <label>Назва</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Наприклад: Каталог"
            style={{ display: "block", width: "100%", padding: "10px" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Тип</label>
          <select
            name="type"
            value={form.type}
            onChange={handleChange}
            style={{ display: "block", width: "100%", padding: "10px" }}
          >
            <option value="url">Зовнішнє посилання</option>
            <option value="file">Файл</option>
          </select>
        </div>

        {form.type === "url" && (
          <div style={{ marginBottom: "15px" }}>
            <label>Посилання</label>
            <input
              name="url"
              value={form.url}
              onChange={handleChange}
              placeholder="https://..."
              style={{ display: "block", width: "100%", padding: "10px" }}
            />
          </div>
        )}

        {form.type === "file" && (
          <div style={{ marginBottom: "15px" }}>
            <label>Файл</label>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ display: "block", marginTop: "8px" }}
            />

            {form.fileName && (
              <p style={{ marginTop: "8px" }}>
                Поточний файл: <b>{form.fileName}</b>
              </p>
            )}

            {form.url && (
              <a href={form.url} target="_blank" rel="noopener noreferrer">
                Переглянути поточний файл
              </a>
            )}
          </div>
        )}

        <div style={{ marginBottom: "15px" }}>
          <label>Порядок</label>
          <input
            name="order"
            type="number"
            value={form.order}
            onChange={handleChange}
            style={{ display: "block", width: "100%", padding: "10px" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>
            <input
              name="isActive"
              type="checkbox"
              checked={form.isActive}
              onChange={handleChange}
            />{" "}
            Активне посилання
          </label>
        </div>

        <button type="submit" disabled={loading}>
          {editingId ? "Оновити" : "Додати"}
        </button>

        {editingId && (
          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            style={{ marginLeft: "10px" }}
          >
            Скасувати
          </button>
        )}
      </form>

      <h3>Поточні посилання</h3>

      <div style={{ display: "grid", gap: "12px" }}>
        {links.map((item) => (
          <div
            key={item.id}
            style={{
              border: "1px solid #ddd",
              padding: "15px",
              borderRadius: "8px",
            }}
          >
            <div>
              <b>{item.title}</b>{" "}
              {!item.isActive && (
                <span style={{ color: "red" }}>(вимкнено)</span>
              )}
            </div>

            <div>Тип: {item.type === "file" ? "Файл" : "Посилання"}</div>
            <div>Порядок: {item.order}</div>

            {item.fileName && <div>Файл: {item.fileName}</div>}

            <a href={item.url} target="_blank" rel="noopener noreferrer">
              Відкрити
            </a>

            <div style={{ marginTop: "10px" }}>
              <button type="button" onClick={() => handleEdit(item)}>
                Редагувати
              </button>

              <button
                type="button"
                onClick={() => handleDelete(item)}
                style={{ marginLeft: "10px" }}
              >
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
